import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  ButtonBase,
  CircularProgress,
  Skeleton,
  Typography,
} from "@mui/material";
import strings from "global/constants/StringConstants";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import { useAppSelector } from "hooks/use-app-select";
import { selectAccount, selectId } from "redux/authSlice";
import {
  fetchAccountDetails,
  updateAccountDetails,
} from "screens/Settings/MyAccount/MyAccountServices";
import {
  cardDescriptionSx,
  cardTitleSx,
  leadingIconSx,
  rowCardSx,
  rowCardTextSx,
  skeletonHeaderTextSx,
  spinnerSx,
  stateLabelSx,
  toggleGroupSx,
  toggleKnobSx,
  toggleTrackSx,
} from "screens/Settings/SecurityPolicies/SecurityPolicies.styles";
import securityPoliciesTokens from "screens/Settings/SecurityPolicies/SecurityPolicies.tokens";
import { MobileDeviceIcon } from "screens/Settings/SecurityPolicies/components/SecurityPoliciesIcons";

const { leadingIcon, skeleton, spinner, toggle } = securityPoliciesTokens;

const ENFORCED_NOTE =
  "Required for everyone — members without MFA will be asked to set it up at next sign-in.";
const OPTIONAL_NOTE =
  "Optional for now — each member decides whether to set up MFA.";

/**
 * "Enforce Multi-Factor Authentication" banner. Reads and writes the
 * account-level `mfaEnabled` flag through the existing account-details API.
 */
const MfaEnforcementCard = () => {
  const userId = useAppSelector(selectId);
  const accountName: string = useAppSelector(selectAccount);

  const [enforced, setEnforced] = useState<boolean>(false);
  const [account, setAccount] = useState<any>(null);
  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const fetchPolicy = useCallback(async () => {
    if (!accountName) return;
    try {
      setFetchLoading(true);
      const response = await fetchAccountDetails(strings.inkfree, accountName);
      setAccount(response);
      setEnforced(!!response?.accountDetails?.mfaEnabled);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setFetchLoading(false);
    }
  }, [accountName]);

  useEffect(() => {
    fetchPolicy();
  }, [fetchPolicy]);

  const handleToggle = async () => {
    const next = !enforced;
    try {
      setSaving(true);
      await updateAccountDetails(
        {
          id: userId,
          name: account?.accountName ?? accountName,
          accountName: account?.accountName ?? accountName,
          accountDisplayName: account?.displayName ?? "",
          mfaEnabled: next,
          pwdExpiration: account?.accountDetails?.pwdExpiration ?? false,
        },
        accountName,
      );
      setEnforced(next);
      openSuccessNotification(
        next
          ? "Multi-factor authentication is now required"
          : "Multi-factor authentication is now optional",
      );
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setSaving(false);
    }
  };

  if (fetchLoading) {
    return (
      <Box sx={rowCardSx}>
        <Skeleton
          variant="rounded"
          width={leadingIcon.size}
          height={leadingIcon.size}
          sx={{ borderRadius: leadingIcon.radius, flexShrink: 0 }}
        />
        <Box sx={skeletonHeaderTextSx}>
          <Skeleton
            variant="text"
            width={skeleton.titleWidth}
            height={skeleton.titleHeight}
          />
          <Skeleton
            variant="text"
            width={skeleton.descriptionWidth}
            height={skeleton.descriptionHeight}
          />
        </Box>
        <Skeleton
          variant="rounded"
          width={toggle.width}
          height={toggle.height}
          sx={{ ml: "auto", flexShrink: 0, borderRadius: "999px" }}
        />
      </Box>
    );
  }

  return (
    <Box sx={rowCardSx}>
      <Box sx={leadingIconSx}>
        <MobileDeviceIcon />
      </Box>
      <Box sx={rowCardTextSx}>
        <Typography component="h2" sx={cardTitleSx}>
          Enforce Multi-Factor Authentication
        </Typography>
        <Typography component="div" sx={cardDescriptionSx(enforced)}>
          {enforced ? ENFORCED_NOTE : OPTIONAL_NOTE}
        </Typography>
      </Box>
      <Box sx={toggleGroupSx}>
        {saving ? (
          <CircularProgress
            size={spinner.size}
            thickness={spinner.thickness}
            sx={spinnerSx}
          />
        ) : (
          <Typography component="span" sx={stateLabelSx}>
            {enforced ? "ON" : "OFF"}
          </Typography>
        )}
        <ButtonBase
          disableRipple
          role="switch"
          aria-checked={enforced}
          aria-label="Enforce Multi-Factor Authentication"
          disabled={saving}
          onClick={handleToggle}
          sx={toggleTrackSx(enforced)}
        >
          <Box sx={toggleKnobSx(enforced)} />
        </ButtonBase>
      </Box>
    </Box>
  );
};

export default MfaEnforcementCard;
